import { useEffect } from "react";
import { IMutator } from "sonobello.utilities.react";

import { SessionContextProps } from "../../../../Context/SessionContext";
import { Appointment, EnhancedAppointment } from "../../../../Models/Appointment";
import { Error } from "../../../../Utils/Errors";
import { useApi } from "../../../../Utils/useApi";
import useSessionCache from "../../../Hooks/useSessionCache";
import SessionContextValueProvider from "../../../Providers/Value/SessionContextValueProvider";
import AppointmentPicker from "../Components/ConfirmationModal/AppointmentPicker";

type IContextProps = Pick<SessionContextProps, "lead" | "customer"> &
  Pick<SessionContextProps["helpers"], "loadFromLocal" | "saveToLocal">;
type IProvisionedAppointmentPickerProps = IContextProps & IMutator<EnhancedAppointment>;

const cacheKey = "medicalEscalationAppointments";

/** Allows for the selection of one appointment from the provided options which are obtained from the API for the current
 * customer.
 */
const ProvisionedAppointmentPicker: React.FC<IProvisionedAppointmentPickerProps> = ({
  lead: { id: leadId },
  customer: { id: customerId, center: customerCenter },
  loadFromLocal,
  onChange,
  saveToLocal
}) => {
  const { value, setValue } = useSessionCache<EnhancedAppointment[]>({ key: cacheKey, loadFromLocal, saveToLocal });

  const { response } = useApi<Appointment[]>({
    url: `Legacy/V0/Contacts/${leadId}/Customers/${customerId}/Appointments?getEmployees=true`,
    valid: Boolean(customerId && !value),
    error: Error.GetCustomerAppointments
  });

  useEffect(() => {
    if (!response || !customerCenter) return;
    setValue(response.map(a => new EnhancedAppointment(a, customerCenter.luxonTimeZone)).filter(a => a.isSurgical));
  });

  return AppointmentPicker({
    options: value || [],
    isLoading: value === null,
    onChange
  });
};

const SessionContextAppointmentPicker: React.FC<IMutator<EnhancedAppointment>> = SessionContextValueProvider<
  IContextProps,
  IProvisionedAppointmentPickerProps
>({
  Component: ProvisionedAppointmentPicker,
  map: ({ lead, customer, helpers: { loadFromLocal, saveToLocal } }) => ({ lead, customer, loadFromLocal, saveToLocal })
});

export default SessionContextAppointmentPicker;
